import { useEbmSaleRecords } from '@hooks/ebm';
import { useAppSelector } from '@hooks/useRedux';
import { queryKeys } from '@lib/queryKeys';
import { selectCurrentUser } from '@store/auth/authSelectors';
import { selectAuthOutletId, selectEffectiveOutletId } from '@store/outlets';
import { useQueryClient } from '@tanstack/react-query';
import { Printer as PrinterIcon, RefreshCw, RotateCw } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { fetchEbmConfig } from '@/renderer/services/ebmConfigService';
import {
  EbmSaleRecord,
  fetchFiscalReceiptLineItems,
  fetchLatestSettlementOnline,
} from '@/renderer/services/ebmSaleRecordsService';
import { getOutletById } from '@/renderer/services/outletService';
import {
  fetchOutletPrinters,
  printFiscalReceipt,
} from '@/renderer/services/printerService';
import { FiscalReceiptTaxBucketPayload } from '@/renderer/types/printer';
import { apiError, apiSuccess } from '@/renderer/util/notify';
import { Button, OutletIndicator } from '@components/common';
import { PaginationControls } from '@components/common/PaginationControls';

const TAX_BUCKET_CODES = ['A', 'B', 'C', 'D'] as const;

function computeLineTax(
  amount: number,
  rate: number,
): { taxblAmt: number; taxAmt: number } {
  const taxAmt = Math.round(((amount * rate) / (100 + rate)) * 100) / 100;
  return { taxblAmt: Math.round((amount - taxAmt) * 100) / 100, taxAmt };
}

const PAGE_SIZE = 20;

const STATUS_BADGE: Record<string, string> = {
  PENDING: 'bg-amber-100 text-amber-700',
  SUBMITTED_QUEUED: 'bg-blue-100 text-blue-700',
  CONFIRMED: 'bg-green-100 text-green-700',
  FAILED: 'bg-red-100 text-red-700',
  OVERRIDDEN_UNCONFIRMED: 'bg-gray-100 text-gray-600',
};

function formatTimestamp(value: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

function RetryButton({
  record,
  canRetry,
  registrantId,
  registrantName,
  onRetried,
}: {
  record: EbmSaleRecord;
  canRetry: boolean;
  registrantId: string;
  registrantName: string;
  onRetried: () => void;
}) {
  const [isRetrying, setIsRetrying] = useState(false);

  if (
    record.submission_status !== 'FAILED' &&
    record.submission_status !== 'PENDING'
  ) {
    return null;
  }

  const handleRetry = async () => {
    setIsRetrying(true);
    try {
      const settlement = await fetchLatestSettlementOnline(record.order_id);
      if (!settlement.success || !settlement.data) {
        apiError('Could not find a settlement for this order.');
        return;
      }
      const result = await window.electron.ipcRenderer.invoke(
        'ebm:submitSale',
        {
          outletId: record.outlet_id,
          orderId: record.order_id,
          settlementId: settlement.data.id,
          paymentMethodId: settlement.data.payment_method_id,
          registrantId,
          registrantName,
          retryEbmSaleRecordId: record.id,
        },
      );
      const response = result as
        | {
            success: boolean;
            error?: string;
            data?: { status: string; errorMessage?: string };
          }
        | undefined;
      if (!response?.success) {
        apiError(response?.error || 'Retry failed.');
      } else if (response.data?.status === 'FAILED') {
        apiError(response.data.errorMessage || 'Retry failed.');
      } else {
        apiSuccess('Retry submitted.');
      }
      onRetried();
    } catch (err) {
      apiError(err instanceof Error ? err.message : 'Retry failed.');
    } finally {
      setIsRetrying(false);
    }
  };

  return (
    <Button
      size="sm"
      variant="outline"
      onClick={handleRetry}
      disabled={!canRetry || isRetrying}
      title={
        canRetry
          ? undefined
          : 'Switch to this record’s outlet on this device to retry.'
      }
    >
      <RotateCw size={13} className={isRetrying ? 'animate-spin' : ''} />
      {isRetrying ? 'Retrying…' : 'Retry'}
    </Button>
  );
}

function PrintReceiptButton({
  record,
  printedBy,
}: {
  record: EbmSaleRecord;
  printedBy: string;
}) {
  const [isPrinting, setIsPrinting] = useState(false);

  if (record.submission_status !== 'CONFIRMED') {
    return null;
  }

  const handlePrint = async () => {
    setIsPrinting(true);
    try {
      const [printersResponse, itemsResponse, outletResponse, configResponse] =
        await Promise.all([
          fetchOutletPrinters(record.outlet_id),
          fetchFiscalReceiptLineItems(record.order_id),
          getOutletById(record.outlet_id),
          fetchEbmConfig(record.outlet_id).catch(() => null),
        ]);

      const printer = printersResponse.data?.find((p) => p.is_active);
      if (!printer) {
        apiError('No active printer configured for this outlet.');
        return;
      }
      if (!itemsResponse.success || !itemsResponse.data) {
        apiError(itemsResponse.error || 'Failed to load order items.');
        return;
      }
      if (!outletResponse.success || !outletResponse.data) {
        apiError(outletResponse.error || 'Failed to load outlet details.');
        return;
      }
      if (
        !record.sdc_id ||
        !record.rcpt_no ||
        !record.rcpt_sign ||
        !record.intrl_data ||
        !record.vsdc_rcpt_pbct_date
      ) {
        apiError(
          'This record is missing SDC fiscal data — cannot print a fiscal receipt.',
        );
        return;
      }

      const buckets = new Map<
        string,
        { rate: number; taxblAmt: number; taxAmt: number }
      >();
      itemsResponse.data.forEach((item) => {
        if (!item.taxCode || item.taxRate === null) return;
        const { taxblAmt, taxAmt } = computeLineTax(item.amount, item.taxRate);
        const existing = buckets.get(item.taxCode) ?? {
          rate: item.taxRate,
          taxblAmt: 0,
          taxAmt: 0,
        };
        buckets.set(item.taxCode, {
          rate: item.taxRate,
          taxblAmt: existing.taxblAmt + taxblAmt,
          taxAmt: existing.taxAmt + taxAmt,
        });
      });

      const taxBuckets: FiscalReceiptTaxBucketPayload[] = TAX_BUCKET_CODES.map(
        (code) => {
          const bucket = buckets.get(code);
          return {
            code,
            rate: bucket?.rate ?? 0,
            taxblAmt: bucket?.taxblAmt ?? 0,
            taxAmt: bucket?.taxAmt ?? 0,
            usedOnThisSale: Boolean(bucket),
          };
        },
      );

      const totalAmount = itemsResponse.data.reduce(
        (sum, item) => sum + item.amount,
        0,
      );

      const response = await printFiscalReceipt({
        printer: {
          name: printer.name,
          connection_type: printer.connection_type,
          printer_identifier: printer.printer_identifier,
          ip_address: printer.ip_address,
          port: printer.port,
          paper_size: printer.paper_size,
          details: printer.details,
        },
        payload: {
          outlet_name: outletResponse.data.name,
          outlet_address: outletResponse.data.address,
          outlet_tin: outletResponse.data.tin_number,
          outlet_contact: outletResponse.data.phone,
          bill_number: record.order_bill_number,
          printed_by: printedBy,
          printed_at: new Date().toISOString(),
          items: itemsResponse.data.map((item) => ({
            name: item.name,
            quantity: item.quantity,
            amount: item.amount,
          })),
          total_amount: totalAmount,
          net_amount: totalAmount,
          taxBuckets,
          sdc: {
            sdcId: record.sdc_id,
            rcptNo: record.rcpt_no,
            totRcptNo: record.tot_rcpt_no,
            rcptSign: record.rcpt_sign,
            intrlData: record.intrl_data,
            vsdcRcptPbctDate: record.vsdc_rcpt_pbct_date,
            rcptTyCd: record.rcpt_ty_cd,
          },
          cis: {
            billNumber: record.order_bill_number,
            confirmedAt: record.confirmed_at ?? new Date().toISOString(),
            posMrcCode: configResponse?.pos_mrc_code ?? null,
          },
        },
      });

      if (!response.success) {
        apiError(response.error || 'Failed to print fiscal receipt.');
      } else {
        apiSuccess('Fiscal receipt sent to printer.');
      }
    } catch (err) {
      apiError(err instanceof Error ? err.message : 'Failed to print receipt.');
    } finally {
      setIsPrinting(false);
    }
  };

  return (
    <Button
      size="sm"
      variant="outline"
      onClick={handlePrint}
      disabled={isPrinting}
    >
      <PrinterIcon size={13} className={isPrinting ? 'animate-pulse' : ''} />
      {isPrinting ? 'Printing…' : 'Print Receipt'}
    </Button>
  );
}

export const EbmSaleRecordsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const currentUser = useAppSelector(selectCurrentUser);
  const outletId = useAppSelector(selectEffectiveOutletId);
  const authOutletId = useAppSelector(selectAuthOutletId);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<'ATTENTION' | 'CONFIRMED'>(
    'ATTENTION',
  );

  const params = useMemo(
    () => ({
      outletId,
      page,
      pageSize: PAGE_SIZE,
      submissionStatus:
        statusFilter === 'CONFIRMED' ? ['CONFIRMED'] : ['FAILED', 'PENDING'],
    }),
    [outletId, page, statusFilter],
  );

  const { data, isLoading, isFetching, refetch } = useEbmSaleRecords(params);

  const rows: EbmSaleRecord[] = data?.data ?? [];
  const isCompanyWide = !outletId;

  const registrantName = currentUser
    ? `${currentUser.first_name} ${currentUser.last_name}`.trim() ||
      currentUser.email
    : 'POS';

  const handleRetried = () => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.ebm.saleRecords(params),
    });
  };

  return (
    <div className="space-y-6">
      <OutletIndicator description="Failed or pending EBM sale submissions. Retrying re-attempts fiscal submission without creating a duplicate invoice." />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">
            EBM Sale Records
          </h1>
          <p className="text-sm text-gray-500">
            {data?.pagination.totalItems ?? 0} record
            {data?.pagination.totalItems === 1 ? '' : 's'}{' '}
            {statusFilter === 'CONFIRMED' ? 'confirmed' : 'need attention'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center rounded-lg border border-gray-200 p-0.5">
            <button
              type="button"
              onClick={() => {
                setStatusFilter('ATTENTION');
                setPage(1);
              }}
              className={`rounded-md px-3 py-1.5 text-sm font-medium ${
                statusFilter === 'ATTENTION'
                  ? 'bg-gray-900 text-white'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              Needs Attention
            </button>
            <button
              type="button"
              onClick={() => {
                setStatusFilter('CONFIRMED');
                setPage(1);
              }}
              className={`rounded-md px-3 py-1.5 text-sm font-medium ${
                statusFilter === 'CONFIRMED'
                  ? 'bg-gray-900 text-white'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              Confirmed
            </button>
          </div>
          <button
            type="button"
            onClick={() => refetch()}
            className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
        <div className="max-h-[32rem] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 border-b border-gray-100 bg-gray-50">
              <tr className="text-left">
                <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Bill #
                </th>
                <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Order Date
                </th>
                <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Invoice No.
                </th>
                <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Status
                </th>
                <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Error
                </th>
                <th className="px-5 py-3" aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-5 py-10 text-center text-sm text-gray-500"
                  >
                    Loading…
                  </td>
                </tr>
              )}
              {!isLoading && rows.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-5 py-10 text-center text-sm text-gray-500"
                  >
                    No{' '}
                    {statusFilter === 'CONFIRMED'
                      ? 'confirmed'
                      : 'failed or pending'}{' '}
                    EBM sale submissions
                    {isCompanyWide ? ' across any outlet' : ' for this outlet'}.
                  </td>
                </tr>
              )}
              {rows.map((record) => (
                <tr key={record.id} className="border-t border-gray-100">
                  <td className="px-5 py-4 font-medium text-gray-900">
                    #{record.order_bill_number}
                  </td>
                  <td className="px-5 py-4 text-gray-600">
                    {record.order_date}
                  </td>
                  <td className="px-5 py-4 text-gray-600">{record.invc_no}</td>
                  <td className="px-5 py-4">
                    <span
                      className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
                        STATUS_BADGE[record.submission_status] ??
                        'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {record.submission_status}
                    </span>
                    {record.result_cd && (
                      <span className="ml-2 text-xs text-gray-400">
                        {record.result_cd}
                      </span>
                    )}
                  </td>
                  <td className="max-w-xs px-5 py-4 text-xs text-gray-500">
                    {record.error_message || '—'}
                    <p className="mt-0.5 text-gray-400">
                      {formatTimestamp(record.submitted_at)}
                    </p>
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-2">
                      <RetryButton
                        record={record}
                        canRetry={record.outlet_id === authOutletId}
                        registrantId={currentUser?.id ?? 'POS'}
                        registrantName={registrantName}
                        onRetried={handleRetried}
                      />
                      <PrintReceiptButton
                        record={record}
                        printedBy={registrantName}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {data && (
        <PaginationControls
          pagination={data.pagination}
          itemLabel="records"
          onPageChange={setPage}
        />
      )}
    </div>
  );
};

export default EbmSaleRecordsPage;
